
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';

import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatButton } from '@angular/material/button';
import {
    MatDialogActions, MatDialogClose,
    MatDialogContent,
    MatDialogRef, MatDialogTitle,
} from '@angular/material/dialog';

import { FsBadgeModule } from '@firestitch/badge';
import { FsDialogModule } from '@firestitch/dialog';
import { ItemType } from '@firestitch/filter';
import { FsListConfig, FsListModule } from '@firestitch/list';
import { FsStore } from '@firestitch/store';

import { of } from 'rxjs';
import { map } from 'rxjs/operators';

import { SessionService } from '@app/core/services';
import { EnvironmentData } from '@common/data';
import { Account, Environment } from '@common/interfaces';

import { SidenavService } from '../../services';


// TU-T8973: InnoDB FULLTEXT ignores tokens shorter than innodb_ft_min_token_size
// (default 3), so a shorter keyword can never match -- gate the search on it.
const KEYWORD_MIN_LENGTH = 3;


@Component({
  templateUrl: './environment-switch.component.html',
  styleUrls: ['./environment-switch.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FsDialogModule,
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FsListModule,
    FsBadgeModule,
    MatDialogActions,
    MatButton,
    MatDialogClose
],
})
export class EnvironmentSwitchComponent implements OnInit {
  private _dialogRef = inject<MatDialogRef<EnvironmentSwitchComponent>>(MatDialogRef);
  private _environmentData = inject(EnvironmentData);
  private _store = inject(FsStore);
  private _router = inject(Router);
  private _sessionService = inject(SessionService);
  private _sidenavService = inject(SidenavService);


  public account: Account = null;
  public listConfig: FsListConfig = null;

  public ngOnInit(): void {
    this._store.observe('account')
      .subscribe((store) => {
        this.account = store.value;
        this._init();
      });
  }

  public switch(environment: Environment): void {
    const isRedirect = this._sessionService.environmentId() !== environment.id;

    this._environmentData.switch(environment.id)
      .subscribe(() => {
        this._sessionService.switchEnvironment();
        this._dialogRef.close();

        if (isRedirect) {
          this._router
            .navigate(
              ['blank'],
              {
                skipLocationChange: true,
              },
            )
            .then(() => {
              this._router.navigateByUrl('/');
            });
        }
      });
  }

  public close(): void {
    this._dialogRef.close();
  }

  private _init(): void {
    this.listConfig = {
      status: false,
      chips: false,
      autoFocus: true,
      loadMore: true,
      filters: [
        {
          name: 'keyword',
          label: 'Search',
          type: ItemType.Keyword,
        },
      ],
      rowEvents: {
        click: (event) => {
          this.switch(event.row);
        },
      },
      fetch: (query) => {
        query = {
          ...query,
          accountId: this.account.id,
        };

        // TU-T8973: don't hit the FULLTEXT search until the keyword reaches the
        // minimum token length -- shorter keywords match nothing and only add load.
        const keyword = (query.keyword ?? '').trim();
        if (keyword && keyword.length < KEYWORD_MIN_LENGTH) {
          return of({ data: [], paging: {} });
        }

        // TU-T8973: rank keyword matches by relevance, most relevant first.
        if (query.keyword && !query.order) {
          query.order = 'relevance';
        }

        return this._environmentData.gets(query, { key: null })
          .pipe(
            map((response) => ({ data: response.environments, paging: response.paging })),
          );
      },
    };
  }

}
